import { LoggerService } from '@angular-ru/cdk/logger';
import { Clipboard } from '@angular/cdk/clipboard';
import { DOCUMENT, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  MatAnchor,
  MatButton,
  MatFabAnchor,
  MatFabButton,
  MatIconAnchor,
  MatIconButton,
  MatMiniFabAnchor,
  MatMiniFabButton,
} from '@angular/material/button';
import { MatDialogRef } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip, TooltipPosition } from '@angular/material/tooltip';
import { take } from 'rxjs/operators';
import { IDialogContent } from 'src/app/api/modules/core/components/dialog/IDialogContent';
import { IDialogMediaLock } from 'src/app/api/modules/core/components/dialog/IDialogMediaLock';
import { ButtonStyleTypes, ButtonTypes, IButton, IButtonFeatureCopyToClipboard } from 'src/app/api/modules/core/dynamic/components/IButton';
import { AppService } from '../../../services/application/application.service';
import { DialogService } from '../../../services/dialog.service';
import { SnackbarService } from '../../../services/snackbar.service';

/**
 * The styles that render a label; everything else is icon-only.
 */
const LABELED_STYLES: readonly ButtonStyleTypes[] = ['basic', 'raised', 'flat', 'stroked'];

const SUPPORTED_STYLES: readonly ButtonStyleTypes[] = ['basic', 'raised', 'flat', 'stroked', 'icon', 'fab', 'mini-fab'];

function isSupportedStyle(style: string): style is ButtonStyleTypes {
  return (SUPPORTED_STYLES as readonly string[]).includes(style);
}

/**
 * The `ButtonComponent` is a reusable button component that supports various configurations,
 * features, and behaviors, making it flexible for different UI requirements.
 *
 * ### Key Features:
 * - Supports both **button** and **link** types, in every Material style
 *   (basic, raised, flat, stroked, icon, fab, mini-fab).
 * - Configured via the `config` input; nothing renders until a truthy config arrives.
 * - Special behaviors: **dialog confirmation**, **copy-to-clipboard**,
 *   **click-to-close**, and **print**.
 * - `isFixedSize` / `isFullSize` are an imperative API (plain setters, not
 *   inputs) used by the button containers to drive host sizing classes.
 *
 * ### References:
 * - See {@link IButton} for button configuration options.
 * - See [Angular Material Button Documentation](https://material.angular.io/components/button/overview).
 */
@Component({
  selector: 'app-button',
  templateUrl: './button.component.html',
  styleUrls: ['./button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatAnchor,
    MatIcon,
    MatIconAnchor,
    MatFabAnchor,
    MatMiniFabAnchor,
    MatButton,
    MatIconButton,
    MatFabButton,
    MatMiniFabButton,
    MatTooltip,
    NgTemplateOutlet,
  ],
  host: {
    '[class.full-size]': 'fullSizeState()',
    '[class.fixed-size]': 'fixedSizeState()',
  },
})
export class ButtonComponent {
  private readonly appService = inject(AppService);
  private readonly dialogService = inject(DialogService);
  private readonly loggerService = inject(LoggerService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly clipboard = inject(Clipboard);
  private readonly documentRef = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);

  /**
   * The configuration object that powers this button.
   *
   * Nothing renders until a truthy config arrives.
   *
   * See {@link IButton} for available configuration options.
   */
  readonly config = input<IButton | null>();

  /**
   * Disables the button, preventing user interaction.
   */
  readonly disabled = input(false);

  /**
   * Tooltip text for the button. When set, it overrides any tooltip from
   * the config (used e.g. by the dev panel to swap tooltips dynamically).
   */
  readonly tooltip = input<string | undefined>();

  /**
   * Emits when the button completes a plain click — i.e. it is not a link,
   * has no feature, and any configured dialog was confirmed.
   */
  readonly buttonClick = output<MouseEvent>();

  /**
   * Sizing flags driven imperatively by the button containers
   * (plain setters rather than inputs — the containers write to bare
   * component instances). They toggle the `fixed-size` / `full-size`
   * host classes.
   */
  protected readonly fixedSizeState = signal(false);
  protected readonly fullSizeState = signal(false);

  set isFixedSize(value: boolean) {
    this.fixedSizeState.set(value);
  }

  set isFullSize(value: boolean) {
    this.fullSizeState.set(value);
  }

  /**
   * Tracks a dialog opened by this button so it can be closed if the button
   * is destroyed while the dialog is open.
   */
  private dialogRef?: MatDialogRef<unknown>;

  /**
   * The validated view state derived from the config, including any
   * configuration warnings (logged once per config change by an effect).
   */
  private readonly viewConfig = computed(() => {
    const configuration = this.config();
    if (!configuration) {
      return undefined;
    }

    const warnings: string[] = [];

    // TYPE — only a real link (with an href) renders as an anchor
    let type: ButtonTypes = 'button';
    if (configuration.type === 'link') {
      if (configuration.link?.href) {
        type = 'link';
      } else {
        warnings.push(`The configured button with 'type: "link"' was requested but no 'link' property was configured.`);
      }
    }

    // STYLE — unknown styles fall back to 'basic'
    let style: ButtonStyleTypes = 'basic';
    if (configuration.style) {
      if (isSupportedStyle(configuration.style)) {
        style = configuration.style;
      } else {
        warnings.push(`The configured button style "${configuration.style}" is not supported and has been changed to "basic".`);
      }
    }

    // extended only applies to full-size FABs
    const extended = style === 'fab' && configuration.extended === true;
    const isIconOnly = style === 'icon' || style === 'mini-fab' || (style === 'fab' && !extended);

    // LABEL
    let label: string | undefined = undefined;
    if (configuration.label) {
      if (isIconOnly) {
        warnings.push(
          `The "label" property is not allowed for buttons with style "icon", "mini-fab", or "fab" with "extended: false". It has been ignored.`,
        );
      } else if (typeof configuration.label === 'string' && configuration.label.length > 0) {
        label = configuration.label;
      }
    } else if (LABELED_STYLES.includes(style)) {
      warnings.push(`The "label" property is required for basic, raised, flat, stroked buttons. It should be added.`);
    }

    // ICONS — icon-only styles show a single icon: prefer the prefix, and
    // promote a lone postfix to the prefix slot
    let iconPrefix = configuration.iconPrefix;
    let iconPostfix = configuration.iconPostfix;
    if (isIconOnly && iconPostfix) {
      if (!iconPrefix) {
        iconPrefix = iconPostfix;
      }
      iconPostfix = undefined;
    }
    if (iconPrefix?.color) {
      warnings.push(`The "iconPrefix.color" property is deprecated on buttons. Remove it from the configuration.`);
    }
    if (iconPostfix?.color) {
      warnings.push(`The "iconPostfix.color" property is deprecated on buttons. Remove it from the configuration.`);
    }
    if (iconPrefix?.align) {
      warnings.push(`The "iconPrefix.align" property is deprecated on buttons. Remove it from the configuration.`);
    }
    if (iconPostfix?.align) {
      warnings.push(`The "iconPostfix.align" property is deprecated on buttons. Remove it from the configuration.`);
    }

    // LINK
    const link = type === 'link' ? configuration.link : undefined;

    // FEATURES — at most one applies; precedence mirrors the legacy chain:
    // copy-to-clipboard, then click-to-close, then print
    const copyToClipboard: IButtonFeatureCopyToClipboard | undefined = configuration.feature?.copyToClipboard;
    const clickToClose = !copyToClipboard && configuration.feature?.clickToClose === true;
    const print = !copyToClipboard && !clickToClose && configuration.feature?.print === true;

    // TOOLTIP / TAB INDEX / ARIA
    const tooltip = configuration.tooltip || undefined;
    const tooltipPosition: TooltipPosition = configuration.tooltipPosition || 'below';
    const tabIndex = typeof configuration.tabIndex === 'number' ? configuration.tabIndex : undefined;
    const ariaLabel = configuration.ariaLabel || undefined;
    if (!ariaLabel && isIconOnly && !label && !tooltip) {
      warnings.push(`Icon-only buttons (icon, mini-fab, fab) should have an "ariaLabel" property for accessibility. Consider adding one.`);
    }

    return {
      type,
      style,
      extended,
      hasLabel: !!label,
      label,
      hasIconPrefix: !!iconPrefix,
      iconPrefixName: iconPrefix?.name ?? '',
      hasIconPostfix: !!iconPostfix,
      iconPostfixName: iconPostfix?.name ?? '',
      link,
      linkHref: link?.href,
      dialog: configuration.dialog,
      copyToClipboard,
      clickToClose,
      print,
      isTextured: configuration.isTextured === true,
      tooltip,
      tooltipPosition,
      tabIndex,
      ariaLabel,
      warnings,
    };
  });

  /**
   * Everything the template renders from.
   */
  protected readonly templateData = computed(() => {
    const view = this.viewConfig();
    if (!view) {
      return undefined;
    }
    return {
      ...view,
      disabled: this.disabled(),
      tooltip: this.tooltip() ?? view.tooltip,
    };
  });

  constructor() {
    // surface configuration warnings (once per config change)
    effect(() => {
      for (const warning of this.viewConfig()?.warnings ?? []) {
        this.loggerService.warn(`[Button] - ${warning}`);
      }
    });

    this.destroyRef.onDestroy(() => {
      // close any dialog this button opened
      this.dialogRef?.close(false);
    });
  }

  ////////////////////////////////////////////////////////////////////////////////
  // TEMPLATE EVENTS
  ////////////////////////////////////////////////////////////////////////////////

  /**
   * Keyboard activation (Enter / Space) for WCAG keyboard accessibility.
   */
  protected onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter' || event.key === ' ') {
      // Suppressed first, on every keydown including auto-repeats: this cancels
      // the browser's own activation behavior — the synthetic click, on both the
      // `<button>` and `<a role="button">` variants — and Space's page scroll.
      // Returning BEFORE this would hand the activation straight back to the
      // browser, so the guard below would buy nothing and holding Space on a
      // link-style button would scroll the page.
      event.preventDefault();

      // A held key is not a series of presses. The OS repeats keydown at its own
      // rate for as long as the key is down, and every repeat ran a full
      // activation — on the link pathway a sustained storm of route resolves,
      // each canceling the last and discarding a response the server had already
      // produced. Holding is an affordance for text entry and sliders, never for
      // a command button.
      if (event.repeat) {
        return;
      }

      this.handleActivation();
    }
  }

  /**
   * Intercepts the native click so conditional behaviors (dialog, features,
   * links) run before — or instead of — the plain click event.
   */
  protected onClick(event: MouseEvent) {
    event.preventDefault();
    this.handleActivation();
  }

  /**
   * Core activation logic shared by mouse and keyboard events.
   *
   * Pathways, in order: disabled (nothing) → link (navigate) → dialog
   * (confirm first) → feature (copy / close / print) → plain click (emit).
   */
  private handleActivation() {
    // register the input
    this.appService.registerFirstInteraction();

    const view = this.viewConfig();
    if (!view) {
      return;
    }

    if (this.disabled()) {
      this.loggerService.info(`[Button] - The button is disabled. No interaction will occur.`);
      return;
    }

    if (view.type === 'link' && view.link) {
      this.appService.openLink(view.link);
      return;
    }

    if (view.dialog) {
      this.processDialog(view.dialog);
      return;
    }

    if (view.copyToClipboard) {
      this.processCopyToClipboard(view.copyToClipboard);
      return;
    }

    if (view.clickToClose) {
      this.appService.registerClose();
      return;
    }

    if (view.print) {
      this.documentRef.defaultView?.print();
      return;
    }

    this.emitButtonClick();
  }

  /**
   * Opens the configured dialog and proceeds with the plain click only when
   * the user confirms (a truthy close result).
   */
  private processDialog(dialog: IDialogContent | IDialogMediaLock) {
    const dialogRef = this.dialogService.openDialog(dialog);

    // the DialogService returns undefined for unrecognized dialog selectors
    if (!dialogRef) {
      return;
    }

    this.dialogRef = dialogRef;
    dialogRef
      .afterClosed()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        this.dialogRef = undefined;
        if (result) {
          this.emitButtonClick();
        }
      });
  }

  /**
   * Copies the configured data to the clipboard (with retries for browsers
   * that defer clipboard access), then shows the configured snackbar.
   * The snackbar announcement also informs screen readers of the result.
   */
  private processCopyToClipboard(copyToClipboard: IButtonFeatureCopyToClipboard) {
    const pending = this.clipboard.beginCopy(copyToClipboard.data);
    let remainingAttempts = 5;
    const attempt = () => {
      const result = pending.copy();
      if (!result && --remainingAttempts) {
        this.documentRef.defaultView?.setTimeout(attempt);
      } else {
        pending.destroy();
        if (copyToClipboard.snackbar) {
          this.snackbarService.open(copyToClipboard.snackbar);
        }
      }
    };
    attempt();
  }

  /**
   * Emits the plain click. A synthetic MouseEvent maintains backward
   * compatibility with the output's type signature.
   */
  private emitButtonClick() {
    this.buttonClick.emit(new MouseEvent('click'));
  }
}
